import React, { useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  useColorScheme,
  Modal,
  Switch,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const GoalScreen = ({ navigation }) => {
  const isDarkMode = useColorScheme() === 'dark';
  const [showAddGoalModal, setShowAddGoalModal] = useState(false);
  const [newGoalData, setNewGoalData] = useState({
    title: '',
    targetValue: '',
    currentValue: '',
    unit: 'kg',
    category: 'weight',
    deadline: '',
  });
  
  const [goals, setGoals] = useState([
    {
      id: '1',
      title: '减重目标',
      category: 'weight',
      targetValue: 65,
      currentValue: 68.5,
      unit: 'kg',
      deadline: '2023-12-31',
      completed: false,
    },
    {
      id: '2',
      title: '每日健走',
      category: 'steps',
      targetValue: 10000,
      currentValue: 7500,
      unit: '步',
      deadline: '持续进行',
      completed: false,
    },
    {
      id: '3',
      title: '改善BMI',
      category: 'bmi',
      targetValue: 22,
      currentValue: 24.5,
      unit: '',
      deadline: '2023-12-31',
      completed: false,
    },
  ]);
  
  const [achievements, setAchievements] = useState([
    {
      id: '1',
      title: '健康起步',
      description: '记录第一个健康数据',
      icon: 'footsteps-outline',
      unlocked: true,
    },
    {
      id: '2',
      title: '坚持一周',
      description: '连续记录7天数据',
      icon: 'calendar-outline',
      unlocked: true,
    },
    {
      id: '3',
      title: '达标一次',
      description: '首次达成一个健康目标',
      icon: 'checkmark-circle-outline',
      unlocked: false,
    },
    {
      id: '4',
      title: '健康专家',
      description: 'BMI达到健康范围',
      icon: 'star-outline',
      unlocked: false,
    },
    {
      id: '5',
      title: '持之以恒',
      description: '连续记录30天数据',
      icon: 'trending-up-outline',
      unlocked: false,
    },
  ]);
  
  // 计算进度百分比
  const calculateProgress = (current, target) => {
    if (target <= current) return 100;
    return Math.round((current / target) * 100);
  };

  // 添加新目标
  const addNewGoal = () => {
    // 验证表单
    if (!newGoalData.title.trim()) {
      Alert.alert('提示', '请输入目标标题');
      return;
    }
    
    if (!newGoalData.targetValue.trim() || isNaN(parseFloat(newGoalData.targetValue))) {
      Alert.alert('提示', '请输入有效的目标值');
      return;
    }
    
    if (!newGoalData.currentValue.trim() || isNaN(parseFloat(newGoalData.currentValue))) {
      Alert.alert('提示', '请输入有效的当前值');
      return;
    }
    
    // 创建新目标
    const newGoal = {
      id: Date.now().toString(),
      title: newGoalData.title.trim(),
      category: newGoalData.category,
      targetValue: parseFloat(newGoalData.targetValue),
      currentValue: parseFloat(newGoalData.currentValue),
      unit: newGoalData.unit,
      deadline: newGoalData.deadline || '持续进行',
      completed: false,
    };
    
    setGoals([...goals, newGoal]);
    setNewGoalData({
      title: '',
      targetValue: '',
      currentValue: '',
      unit: 'kg',
      category: 'weight',
      deadline: '',
    });
    setShowAddGoalModal(false);
  };

  // 更新目标进度
  const updateGoalProgress = (id, newValue) => {
    const updatedGoals = goals.map(goal => {
      if (goal.id === id) {
        const parsedValue = parseFloat(newValue);
        if (!isNaN(parsedValue)) {
          // 检查是否已经完成目标
          let completed = goal.completed;
          if (goal.category === 'weight' || goal.category === 'bmi') {
            completed = parsedValue <= goal.targetValue;
          } else {
            completed = parsedValue >= goal.targetValue;
          }
          
          return {
            ...goal,
            currentValue: parsedValue,
            completed
          };
        }
      }
      return goal;
    });
    
    setGoals(updatedGoals);
    
    // 检查是否需要解锁成就
    const hasCompletedGoal = updatedGoals.some(goal => goal.completed);
    if (hasCompletedGoal) {
      const updatedAchievements = achievements.map(achievement => {
        if (achievement.id === '3' && !achievement.unlocked) {
          return { ...achievement, unlocked: true };
        }
        return achievement;
      });
      
      setAchievements(updatedAchievements);
    }
  };

  // 更新目标状态
  const toggleGoalCompletion = (id) => {
    const updatedGoals = goals.map(goal => {
      if (goal.id === id) {
        return { ...goal, completed: !goal.completed };
      }
      return goal;
    });
    
    setGoals(updatedGoals);
  };

  return (
    <ScrollView 
      style={{
        flex: 1,
        backgroundColor: isDarkMode ? '#8fa0c1' : '#ffffff',
      }}
      contentContainerStyle={{ padding: 16 }}
    >
      {/* 健康目标 */}
      <View style={{
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        backgroundColor: isDarkMode ? '#a9b6d0' : 'white',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 2,
      }}>
        <View style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}>
          <Text style={{
            fontSize: 18,
            fontWeight: 'bold',
            color: isDarkMode ? 'white' : '#1f2937',
          }}>
            健康目标
          </Text>
          
          <TouchableOpacity
            onPress={() => setShowAddGoalModal(true)}
            style={{
              backgroundColor: '#2563eb',
              paddingHorizontal: 12,
              paddingVertical: 6,
              borderRadius: 6,
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Ionicons name="add" size={16} color="white" />
            <Text style={{ color: 'white', fontWeight: '500', marginLeft: 4 }}>
              添加目标
            </Text>
          </TouchableOpacity>
        </View>
        
        {goals.length === 0 ? (
          <View style={{
            padding: 40,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
            borderRadius: 8,
          }}>
            <Ionicons name="flag-outline" size={48} color={isDarkMode ? '#cbd5e1' : '#9ca3af'} />
            <Text style={{
              marginTop: 12,
              color: isDarkMode ? '#cbd5e1' : '#6b7280',
              textAlign: 'center',
            }}>
              还没有设定健康目标
            </Text>
            <TouchableOpacity
              onPress={() => setShowAddGoalModal(true)}
              style={{
                marginTop: 16,
                backgroundColor: '#2563eb',
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: 8,
              }}
            >
              <Text style={{ color: 'white' }}>立即添加</Text>
            </TouchableOpacity>
          </View>
        ) : (
          goals.map((goal) => (
            <View 
              key={goal.id} 
              style={{
                marginBottom: 16,
                padding: 16,
                borderRadius: 8,
                backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
                borderLeftWidth: 4,
                borderLeftColor: goal.completed ? '#10b981' : '#2563eb',
              }}
            >
              <View style={{ 
                flexDirection: 'row', 
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}>
                <Text style={{ 
                  fontSize: 16, 
                  fontWeight: 'bold',
                  color: isDarkMode ? 'white' : '#1f2937',
                }}>
                  {goal.title}
                </Text>
                <Switch
                  value={goal.completed}
                  onValueChange={() => toggleGoalCompletion(goal.id)}
                  trackColor={{ false: isDarkMode ? '#657084' : '#e5e7eb', true: '#10b981' }}
                  thumbColor={goal.completed ? '#ffffff' : '#f3f4f6'}
                />
              </View>
              
              <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: 12,
              }}>
                <Text style={{ color: isDarkMode ? '#cbd5e1' : '#6b7280' }}>
                  {goal.category === 'weight' ? '目标体重' : 
                   goal.category === 'bmi' ? '目标BMI' : 
                   goal.category === 'steps' ? '目标步数' : '目标值'}:
                  {' '}{goal.targetValue}{goal.unit}
                </Text>
                <Text style={{ color: isDarkMode ? '#cbd5e1' : '#6b7280' }}>
                  截止日期: {goal.deadline}
                </Text>
              </View>
              
              <View style={{ marginBottom: 12 }}>
                <View style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  marginBottom: 6,
                }}>
                  <Text style={{ color: isDarkMode ? '#e0e0e0' : '#4b5563' }}>
                    进度
                  </Text>
                  <Text style={{ color: isDarkMode ? '#e0e0e0' : '#4b5563' }}>
                    {goal.currentValue}{goal.unit} / {goal.targetValue}{goal.unit}
                  </Text>
                </View>
                
                <View style={{
                  height: 8,
                  backgroundColor: isDarkMode ? '#657084' : '#e5e7eb',
                  borderRadius: 4,
                  overflow: 'hidden',
                }}>
                  <View style={{
                    height: '100%',
                    width: `${calculateProgress(
                      goal.category === 'weight' || goal.category === 'bmi' 
                        ? Math.min(goal.targetValue, goal.currentValue) / goal.targetValue * 100
                        : Math.min(goal.currentValue, goal.targetValue) / goal.targetValue * 100
                    )}%`,
                    backgroundColor: goal.completed ? '#10b981' : '#2563eb',
                    borderRadius: 4,
                  }} />
                </View>
              </View>
              
              <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <Text style={{ color: isDarkMode ? '#cbd5e1' : '#6b7280' }}>
                  更新进度:
                </Text>
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                }}>
                  <TextInput
                    keyboardType="numeric"
                    placeholder={goal.currentValue.toString()}
                    placeholderTextColor={isDarkMode ? '#8295b7' : '#9ca3af'}
                    onEndEditing={(e) => updateGoalProgress(goal.id, e.nativeEvent.text)}
                    style={{
                      width: 60,
                      textAlign: 'center',
                      padding: 6,
                      marginRight: 8,
                      backgroundColor: isDarkMode ? '#cbd5e1' : 'white',
                      borderRadius: 4,
                      color: '#1f2937',
                    }}
                  />
                  <Text style={{ color: isDarkMode ? '#e0e0e0' : '#4b5563' }}>
                    {goal.unit}
                  </Text>
                </View>
              </View>
            </View>
          ))
        )}
      </View>
      
      {/* 成就系统 */}
      <View style={{
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        backgroundColor: isDarkMode ? '#a9b6d0' : 'white',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 2,
      }}>
        <Text style={{
          fontSize: 18,
          fontWeight: 'bold',
          marginBottom: 16,
          color: isDarkMode ? 'white' : '#1f2937',
        }}>
          成就徽章
        </Text>
        
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          {achievements.map((achievement) => (
            <View 
              key={achievement.id}
              style={{
                width: '48%',
                marginBottom: 16,
                padding: 16,
                borderRadius: 8,
                backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
                alignItems: 'center',
                opacity: achievement.unlocked ? 1 : 0.5,
              }}
            >
              <View style={{
                width: 60,
                height: 60,
                borderRadius: 30,
                backgroundColor: achievement.unlocked ? '#fbbf24' : isDarkMode ? '#657084' : '#d1d5db',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 12,
              }}>
                <Ionicons 
                  name={achievement.icon} 
                  size={30} 
                  color={achievement.unlocked ? 'white' : isDarkMode ? '#96a3b0' : '#9ca3af'} 
                />
              </View>
              <Text style={{
                fontWeight: 'bold',
                marginBottom: 4,
                textAlign: 'center',
                color: isDarkMode ? 'white' : '#1f2937',
              }}>
                {achievement.title}
              </Text>
              <Text style={{
                fontSize: 12,
                textAlign: 'center',
                color: isDarkMode ? '#cbd5e1' : '#6b7280',
              }}>
                {achievement.description}
              </Text>
            </View>
          ))}
        </View>
      </View>
      
      {/* 添加目标模态框 */}
      <Modal
        visible={showAddGoalModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowAddGoalModal(false)}
      >
        <View style={{
          flex: 1,
          justifyContent: 'flex-end',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}>
          <View style={{
            padding: 16,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            backgroundColor: isDarkMode ? '#a9b6d0' : 'white',
          }}>
            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              marginBottom: 16,
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              添加健康目标
            </Text>
            
            {/* 目标标题 */}
            <View style={{ marginBottom: 16 }}>
              <Text style={{
                marginBottom: 8,
                color: isDarkMode ? '#e0e0e0' : '#4b5563',
              }}>
                目标标题
              </Text>
              <TextInput
                value={newGoalData.title}
                onChangeText={(text) => setNewGoalData({...newGoalData, title: text})}
                placeholder="例：减轻体重"
                placeholderTextColor={isDarkMode ? '#8295b7' : '#9ca3af'}
                style={{
                  padding: 12,
                  borderWidth: 1,
                  borderColor: isDarkMode ? '#84929e' : '#e5e7eb',
                  borderRadius: 8,
                  color: isDarkMode ? 'white' : '#1f2937',
                  backgroundColor: isDarkMode ? '#8295b7' : 'white',
                }}
              />
            </View>
            
            {/* 目标类型 */}
            <View style={{ marginBottom: 16 }}>
              <Text style={{
                marginBottom: 8,
                color: isDarkMode ? '#e0e0e0' : '#4b5563',
              }}>
                目标类型
              </Text>
              <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}>
                <TouchableOpacity
                  onPress={() => setNewGoalData({...newGoalData, category: 'weight', unit: 'kg'})}
                  style={{
                    flex: 1,
                    paddingVertical: 10,
                    paddingHorizontal: 4,
                    backgroundColor: newGoalData.category === 'weight' ? '#2563eb' : (isDarkMode ? '#8295b7' : '#f3f4f6'),
                    alignItems: 'center',
                    borderRadius: 8,
                    marginRight: 8,
                  }}
                >
                  <Text style={{
                    color: newGoalData.category === 'weight' ? 'white' : (isDarkMode ? '#e0e0e0' : '#4b5563'),
                  }}>
                    体重
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  onPress={() => setNewGoalData({...newGoalData, category: 'bmi', unit: ''})}
                  style={{
                    flex: 1,
                    paddingVertical: 10,
                    paddingHorizontal: 4,
                    backgroundColor: newGoalData.category === 'bmi' ? '#2563eb' : (isDarkMode ? '#8295b7' : '#f3f4f6'),
                    alignItems: 'center',
                    borderRadius: 8,
                    marginHorizontal: 4,
                  }}
                >
                  <Text style={{
                    color: newGoalData.category === 'bmi' ? 'white' : (isDarkMode ? '#e0e0e0' : '#4b5563'),
                  }}>
                    BMI
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  onPress={() => setNewGoalData({...newGoalData, category: 'steps', unit: '步'})}
                  style={{
                    flex: 1,
                    paddingVertical: 10,
                    paddingHorizontal: 4,
                    backgroundColor: newGoalData.category === 'steps' ? '#2563eb' : (isDarkMode ? '#8295b7' : '#f3f4f6'),
                    alignItems: 'center',
                    borderRadius: 8,
                    marginLeft: 8,
                  }}
                >
                  <Text style={{
                    color: newGoalData.category === 'steps' ? 'white' : (isDarkMode ? '#e0e0e0' : '#4b5563'),
                  }}>
                    步数
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
            
            {/* 目标值 */}
            <View style={{ marginBottom: 16 }}>
              <Text style={{
                marginBottom: 8,
                color: isDarkMode ? '#e0e0e0' : '#4b5563',
              }}>
                目标值
              </Text>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <TextInput
                  value={newGoalData.targetValue}
                  onChangeText={(text) => setNewGoalData({...newGoalData, targetValue: text})}
                  keyboardType="numeric"
                  placeholder={
                    newGoalData.category === 'weight' ? "例：65" :
                    newGoalData.category === 'bmi' ? "例：22" : "例：10000"
                  }
                  placeholderTextColor={isDarkMode ? '#8295b7' : '#9ca3af'}
                  style={{
                    flex: 1,
                    padding: 12,
                    borderWidth: 1,
                    borderColor: isDarkMode ? '#84929e' : '#e5e7eb',
                    borderRadius: 8,
                    color: isDarkMode ? 'white' : '#1f2937',
                    backgroundColor: isDarkMode ? '#8295b7' : 'white',
                  }}
                />
                {newGoalData.unit && (
                  <Text style={{
                    marginLeft: 8,
                    color: isDarkMode ? '#e0e0e0' : '#4b5563',
                  }}>
                    {newGoalData.unit}
                  </Text>
                )}
              </View>
            </View>
            
            {/* 当前值 */}
            <View style={{ marginBottom: 16 }}>
              <Text style={{
                marginBottom: 8,
                color: isDarkMode ? '#e0e0e0' : '#4b5563',
              }}>
                当前值
              </Text>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <TextInput
                  value={newGoalData.currentValue}
                  onChangeText={(text) => setNewGoalData({...newGoalData, currentValue: text})}
                  keyboardType="numeric"
                  placeholder="当前实际值"
                  placeholderTextColor={isDarkMode ? '#8295b7' : '#9ca3af'}
                  style={{
                    flex: 1,
                    padding: 12,
                    borderWidth: 1,
                    borderColor: isDarkMode ? '#84929e' : '#e5e7eb',
                    borderRadius: 8,
                    color: isDarkMode ? 'white' : '#1f2937',
                    backgroundColor: isDarkMode ? '#8295b7' : 'white',
                  }}
                />
                {newGoalData.unit && (
                  <Text style={{
                    marginLeft: 8,
                    color: isDarkMode ? '#e0e0e0' : '#4b5563',
                  }}>
                    {newGoalData.unit}
                  </Text>
                )}
              </View>
            </View>
            
            {/* 截止时间 */}
            <View style={{ marginBottom: 24 }}>
              <Text style={{
                marginBottom: 8,
                color: isDarkMode ? '#e0e0e0' : '#4b5563',
              }}>
                截止时间 (选填)
              </Text>
              <TextInput
                value={newGoalData.deadline}
                onChangeText={(text) => setNewGoalData({...newGoalData, deadline: text})}
                placeholder="例：2023-12-31 (留空表示持续进行)"
                placeholderTextColor={isDarkMode ? '#8295b7' : '#9ca3af'}
                style={{
                  padding: 12,
                  borderWidth: 1,
                  borderColor: isDarkMode ? '#84929e' : '#e5e7eb',
                  borderRadius: 8,
                  color: isDarkMode ? 'white' : '#1f2937',
                  backgroundColor: isDarkMode ? '#8295b7' : 'white',
                }}
              />
            </View>
            
            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <TouchableOpacity
                onPress={() => setShowAddGoalModal(false)}
                style={{
                  flex: 1,
                  paddingVertical: 12,
                  backgroundColor: isDarkMode ? '#657084' : '#f3f4f6',
                  borderRadius: 8,
                  marginRight: 8,
                }}
              >
                <Text style={{
                  textAlign: 'center',
                  fontWeight: '600',
                  color: isDarkMode ? '#e0e0e0' : '#4b5563',
                }}>
                  取消
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={addNewGoal}
                style={{
                  flex: 1,
                  paddingVertical: 12,
                  backgroundColor: '#2563eb',
                  borderRadius: 8,
                  marginLeft: 8,
                }}
              >
                <Text style={{
                  textAlign: 'center',
                  fontWeight: '600',
                  color: 'white',
                }}>
                  添加
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

export default GoalScreen; 